import { useState } from 'react'
import Icon from '../components/Icon'
import { worx } from './client'
import { celebrate } from './feel'
import { tw, type WorxKey } from './strings'

const KINDS = [
  { id: 'idea', label: 'w.fb.idea' },
  { id: 'problem', label: 'w.fb.problem' },
  { id: 'love', label: 'w.fb.love' },
] as const satisfies readonly { id: string; label: WorxKey }[]

/**
 * The light-bulb tab.
 *
 * ── It goes to the queue that already exists ───────────────────────────────
 *
 * Feedback is filed as a `support` report with the reason `feedback`, the same
 * way Get Help is: the administrators are already notified of those and
 * already have a screen for them, so a second table would be a second place
 * to forget to look. The kind the person picked goes at the front of the text,
 * where whoever reads the queue sees it first.
 */
export default function Feedback() {
  const [kind, setKind] = useState<(typeof KINDS)[number]['id']>('idea')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send(e: React.FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    setBusy(true)
    setError(null)
    try {
      await worx.report({ subjectType: 'support', subjectId: 'feedback', reason: 'feedback', detail: `[${kind}] ${text.trim()}` })
      setText('')
      celebrate({ title: tw('w.cheer.feedback'), line: tw('w.cheer.feedbackLine') })
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.send'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card w-feedback" onSubmit={send}>
      <h2><Icon name="bulb" size={26} /> {tw('w.fb.title')}</h2>
      <p className="muted">{tw('w.fb.hint')}</p>

      <div className="w-chip-row" role="group" aria-label={tw('w.fb.title')}>
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            className={kind === k.id ? 'w-chip on' : 'w-chip'}
            aria-pressed={kind === k.id}
            onClick={() => setKind(k.id)}
          >
            {tw(k.label)}
          </button>
        ))}
      </div>

      <textarea
        rows={6}
        value={text}
        maxLength={1900}
        placeholder={tw('w.fb.placeholder')}
        aria-label={tw('w.fb.title')}
        onChange={(e) => setText(e.target.value)}
      />
      {error && <p className="error" role="alert">{error}</p>}
      <button className="primary" disabled={busy || !text.trim()}>
        <Icon name="mail" size={18} /> {busy ? '…' : tw('w.fb.send')}
      </button>
    </form>
  )
}
